import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { formatCLP, timeAgo } from "../lib/format";
import { RouteMap } from "./RouteMap";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { PaperPlaneRight, CircleNotch, Printer, MapPin } from "@phosphor-icons/react";

export function LiveTracking({ serviceId, active, isCarrier }) {
  const [tracking, setTracking] = useState(null);
  const [sharing, setSharing] = useState(false);
  const watchRef = useRef(null);

  const loadT = () => api.get(`/services/${serviceId}/tracking`).then((r) => setTracking(r.data)).catch(() => {});
  useEffect(() => { loadT(); const i = setInterval(loadT, 15000); return () => clearInterval(i); }, [serviceId]);
  useEffect(() => () => { if (watchRef.current) navigator.geolocation.clearWatch(watchRef.current); }, []);

  const toggleShare = () => {
    if (sharing) {
      if (watchRef.current) navigator.geolocation.clearWatch(watchRef.current);
      setSharing(false); return;
    }
    if (!navigator.geolocation) return toast.error("Tu dispositivo no permite geolocalización");
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => api.post(`/services/${serviceId}/location`, { lat: pos.coords.latitude, lng: pos.coords.longitude }).then(loadT).catch(() => {}),
      () => toast.error("No pudimos obtener tu ubicación (revisa permisos y usa HTTPS)"),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
    );
    setSharing(true);
    toast.success("Compartiendo ubicación en vivo");
  };

  const t = tracking || {};
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5 mb-4" data-testid="live-tracking">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold flex items-center gap-2"><MapPin size={20} className="text-[#FF5A36]" /> Seguimiento en vivo</h2>
        {isCarrier && active && (
          <button onClick={toggleShare} data-testid="toggle-share-location"
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${sharing ? "bg-emerald-600 text-white" : "bg-[#0F1115] text-white hover:bg-[#1A1D24]"}`}>
            {sharing ? "Compartiendo… (detener)" : "Compartir mi ubicación"}
          </button>
        )}
      </div>
      {(t.origin?.lat != null || t.destination?.lat != null || t.lastLocation?.lat != null) ? (
        <RouteMap origin={t.origin} destination={t.destination} live={t.lastLocation} height={220} />
      ) : <p className="text-sm text-zinc-500">Aún no hay ubicación disponible.</p>}
      {t.lastLocation?.updatedAt && <p className="text-xs text-zinc-400 mt-2">Última ubicación del transportista: {timeAgo(t.lastLocation.updatedAt)}</p>}
    </div>
  );
}

export function ServiceChat({ serviceId, userId }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);
  const load = () => api.get(`/services/${serviceId}/messages`).then((r) => setMsgs(r.data)).catch(() => {});
  useEffect(() => { load(); const i = setInterval(load, 6000); return () => clearInterval(i); }, [serviceId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setText("");
    try { await api.post(`/services/${serviceId}/messages`, { text: t }); load(); }
    catch { toast.error("No se pudo enviar"); }
  };

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5 mb-4" data-testid="service-chat">
      <h2 className="font-semibold mb-3 flex items-center gap-2"><PaperPlaneRight size={20} className="text-[#FF5A36]" /> Chat</h2>
      <div className="max-h-56 overflow-y-auto space-y-2 mb-3" data-testid="chat-messages">
        {msgs.length === 0 && <p className="text-sm text-zinc-400 text-center py-6">Aún no hay mensajes. Coordina la entrega por aquí.</p>}
        {msgs.map((m) => {
          const mine = m.senderId === userId;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] px-3.5 py-2 rounded-2xl text-sm ${mine ? "bg-[#FF5A36] text-white rounded-br-sm" : "bg-zinc-100 text-zinc-800 rounded-bl-sm"}`}>
                {m.text}
                <div className={`text-[10px] mt-0.5 ${mine ? "text-white/70" : "text-zinc-400"}`}>{timeAgo(m.createdAt)}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2">
        <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Escribe un mensaje…" className="flex-1 h-11 px-4 rounded-xl border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A36]" />
        <button onClick={send} data-testid="chat-send" className="px-4 rounded-xl bg-[#0F1115] text-white font-semibold hover:bg-[#1A1D24] active:scale-95"><PaperPlaneRight size={18} weight="bold" /></button>
      </div>
    </div>
  );
}

export function DispatchGuide({ serviceId, svc, isClient, config, onSaved }) {
  const [form, setForm] = useState({ type: svc.dispatchGuide?.type || "", folio: svc.dispatchGuide?.folio || "", emitterRut: svc.dispatchGuide?.emitterRut || "" });
  const [busy, setBusy] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const save = async () => {
    if (!form.type || !form.folio) return toast.error("Indica el tipo y el folio del documento");
    setBusy(true);
    try { await api.post(`/services/${serviceId}/dispatch-guide`, form); toast.success("Documento de traslado guardado"); onSaved && onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5 mb-4" data-testid="dispatch-guide">
      <h2 className="font-semibold mb-1">Documentación en ruta</h2>
      <p className="text-sm text-zinc-500 mb-3">Guía de Despacho o Factura que ampara el traslado (SII Res. 154).</p>
      {isClient ? (
        <div className="space-y-3">
          <select data-testid="dg-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
            className="w-full h-12 px-4 rounded-xl border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36]">
            <option value="">Tipo de documento</option>
            {(config.dispatchGuideTypes || []).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input data-testid="dg-folio" value={form.folio} onChange={(e) => setForm({ ...form, folio: e.target.value })} placeholder="N° de folio"
            className="w-full h-12 px-4 rounded-xl border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A36]" />
          <input data-testid="dg-rut" value={form.emitterRut} onChange={(e) => setForm({ ...form, emitterRut: e.target.value })} placeholder="RUT emisor (opcional)"
            className="w-full h-12 px-4 rounded-xl border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A36]" />
          <button onClick={save} disabled={busy} data-testid="dg-save"
            className="px-5 py-2.5 rounded-full bg-[#FF5A36] text-white text-sm font-semibold hover:bg-[#E04B2A] disabled:opacity-60 active:scale-95">Guardar documento</button>
        </div>
      ) : (
        svc.dispatchGuide ? <div className="text-sm font-medium" data-testid="dg-readonly">{svc.dispatchGuide.type} · Folio {svc.dispatchGuide.folio}</div>
          : <p className="text-sm text-zinc-400">El cliente aún no adjunta la documentación de traslado.</p>
      )}
      <button onClick={() => setSheetOpen(true)} data-testid="open-route-sheet" className="mt-4 text-sm font-semibold text-[#FF5A36] hover:underline">Ver hoja de ruta →</button>
      <RouteSheetDialog serviceId={serviceId} open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{label}</div>
      <div className="text-sm font-medium">{value || "—"}</div>
    </div>
  );
}

export function RouteSheetDialog({ serviceId, open, onClose }) {
  const [sheet, setSheet] = useState(null);
  useEffect(() => { if (open) { setSheet(null); api.get(`/services/${serviceId}/route-sheet`).then((r) => setSheet(r.data)).catch(() => {}); } }, [open, serviceId]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="route-sheet-dialog">
        <DialogHeader><DialogTitle>Hoja de ruta</DialogTitle></DialogHeader>
        {!sheet ? <div className="py-8 text-center"><CircleNotch size={28} className="animate-spin text-[#FF5A36] mx-auto" /></div> : (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Transportista" value={sheet.transporter?.name} />
              <Field label="RUT transportista" value={sheet.transporter?.rut} />
              <Field label="RNSTC" value={sheet.transporter?.rnstc} />
              <Field label="Licencia" value={sheet.driver?.licenseClass} />
              <Field label="Conductor" value={sheet.driver?.name} />
              <Field label="Cédula conductor" value={sheet.driver?.cedula} />
              <Field label="Patente" value={sheet.vehicle?.plate} />
              <Field label="Vehículo" value={sheet.vehicle?.type} />
              <Field label="Origen" value={`${sheet.origin?.address || ""}, ${sheet.origin?.comuna || sheet.origin?.city || ""}`} />
              <Field label="Destino" value={`${sheet.destination?.address || ""}, ${sheet.destination?.comuna || sheet.destination?.city || ""}`} />
              <Field label="Carga" value={sheet.cargo?.category} />
              <Field label="Peso aprox." value={sheet.cargo?.weight} />
              <Field label="Documento traslado" value={sheet.dispatchGuide ? `${sheet.dispatchGuide.type} · ${sheet.dispatchGuide.folio}` : "Pendiente"} />
              <Field label="Precio acordado" value={formatCLP(sheet.agreedPrice)} />
            </div>
            <button onClick={() => window.print()} data-testid="print-route-sheet"
              className="w-full py-3 rounded-xl bg-[#0F1115] text-white font-semibold hover:bg-[#1A1D24] flex items-center justify-center gap-2"><Printer size={18} /> Imprimir</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
